import CurrencyInput from 'react-currency-input-field';
import { FieldShell } from './FieldShell';
import { useDomFieldName, useDomFieldId } from './_useFieldName';
import { FIELD_AUTOCOMPLETE_OFF, FIELD_NO_AUTOFILL } from './_fieldDefaults';

const BLOCK = 'LStudio__app_field';

/*
    🔥 MONEDA — react-currency-input-field

    Por dentro es un <input> común, así que toma nuestras clases
    directo y no necesita override de CSS.

    Por defecto en formato argentino: punto para miles, coma decimal.

    ⚠️ EL LÍMITE DE DÍGITOS

    Sin tope, el usuario puede tipear "999.999.999.999.999.999" y el
    campo lo acepta feliz. Eso rompe la grilla, desborda el `int` del
    backend y casi siempre es un error de tipeo, no una intención.

    `maxDigits` limita la PARTE ENTERA, no el string completo: nueve
    dígitos son novecientos millones, que alcanza para cualquier
    precio y para la mayoría de los montos contables.

    El control se hace acá y no con el maxLength del input porque
    maxLength contaría también los puntos de miles y la coma: con
    9 dígitos reales el string llega a 14 caracteres.
*/

export const FieldCurrency = ({
    id, name, label, hint, error,
    value, onChange,
    prefix = '$ ', decimals = 2,
    groupSeparator = '.', decimalSeparator = ',',
    maxDigits = 9,
    placeholder = '$ 0,00',
    isRequired = false, isDisabled = false, isReadOnly = false,
    span = 1, ...rest
}) => {

    const onHandleChange = (nextValue, nextName, values) => {

        if (!maxDigits) return onChange?.(nextValue, nextName, values);

        // La parte entera, sin separadores ni signo
        const INTEGER = String(nextValue ?? '')
            .split(decimalSeparator)[0]
            .replace(/\D/g, '');

        // Si se pasó del tope, ignoramos el cambio: el valor no avanza
        if (INTEGER.length > maxDigits) return;

        onChange?.(nextValue, nextName, values);
    };

    const MAX_LABEL = maxDigits
        ? `Hasta ${maxDigits} dígitos (${prefix}${'9'.repeat(maxDigits).replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)})`
        : null;

    const DOM_NAME = useDomFieldName();

    const DOM_ID = useDomFieldId(id);

    return (
        <FieldShell id={DOM_ID} label={label} error={error} span={span}
            isRequired={isRequired} isDisabled={isDisabled}
            hint={hint ?? MAX_LABEL} >

            <CurrencyInput
                id={DOM_ID} name={DOM_NAME}
                className={`${BLOCK}__input`}
                value={value} onValueChange={onHandleChange}
                prefix={prefix}
                decimalsLimit={decimals}
                groupSeparator={groupSeparator}
                decimalSeparator={decimalSeparator}
                placeholder={placeholder}
                allowNegativeValue={false}
                required={isRequired} disabled={isDisabled} readOnly={isReadOnly}
                aria-invalid={!!error}
                inputMode="decimal"
                autoComplete={FIELD_AUTOCOMPLETE_OFF}
                {...FIELD_NO_AUTOFILL}
                {...rest}
            />

        </FieldShell>
    );
};
